import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2, Plus, Trash2, Download, Printer, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import API from '@/config/api';
// API imported from config;

const StaffRecordForm = ({ open, onClose, staff, staffName, token }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');
  
  const [formData, setFormData] = useState({
    date_of_birth: '',
    address: '',
    phone: '',
    emergency_contacts: [
      { name: '', address: '', phone: '' },
      { name: '', address: '', phone: '' }
    ],
    has_high_school: null,
    high_school_date: '',
    high_school_name: '',
    has_ged: null,
    ged_date: '',
    ged_agency: '',
    education_entries: [],
    work_experience: [],
    license_revoked: null,
    license_revoked_details: '',
    provides_infant_care: null,
    transports_children: null,
    counted_in_ratios: null,
    employment_status: 'full_time'
  });

  useEffect(() => {
    if (open && staff?.id) {
      loadData();
    }
  }, [open, staff?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/staff/${staff.id}/staff-record-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          ...response.data,
          emergency_contacts: response.data.emergency_contacts?.length >= 2 
            ? response.data.emergency_contacts 
            : [
                response.data.emergency_contacts?.[0] || { name: '', address: '', phone: '' },
                response.data.emergency_contacts?.[1] || { name: '', address: '', phone: '' }
              ]
        }));
      }
    } catch (error) {
      console.error('Error loading staff record data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/staff/${staff.id}/staff-record-data`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staff record saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save staff record');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/staff/${staff.id}/forms/dcf-0053`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DCF-F-CFS0053_${staffName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePrint = async () => {
    try {
      const response = await axios.get(`${API}/staff/${staff.id}/forms/dcf-0053`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url);
      printWindow.onload = () => printWindow.print();
    } catch (error) {
      toast.error('Failed to open for printing');
    }
  };

  const updateEmergencyContact = (index, field, value) => {
    const updated = [...formData.emergency_contacts];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, emergency_contacts: updated });
  };

  const addEducationEntry = () => {
    setFormData({
      ...formData,
      education_entries: [
        ...formData.education_entries,
        { id: Date.now().toString(), institution: '', dates_attended: '', major: '', degree: '' }
      ]
    });
  };

  const updateEducationEntry = (index, field, value) => {
    const updated = [...formData.education_entries];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, education_entries: updated });
  };

  const removeEducationEntry = (index) => {
    setFormData({
      ...formData,
      education_entries: formData.education_entries.filter((_, i) => i !== index)
    });
  };

  const addWorkExperience = () => {
    setFormData({
      ...formData,
      work_experience: [
        ...formData.work_experience,
        { id: Date.now().toString(), employer: '', address: '', phone: '', dates: '', position: '', duties: '', days_per_week: '', reason_for_leaving: '' }
      ]
    });
  };

  const updateWorkExperience = (index, field, value) => {
    const updated = [...formData.work_experience];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, work_experience: updated });
  };

  const removeWorkExperience = (index) => {
    setFormData({
      ...formData,
      work_experience: formData.work_experience.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="staff-record-form-dialog">
        <DialogHeader>
          <DialogTitle>Staff Record (DCF-F-CFS0053) - {staffName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm bg-muted/50 p-3 rounded-lg">
            <div><span className="font-medium">Name:</span> {staffName}</div>
            <div><span className="font-medium">Position:</span> {staff?.position || 'N/A'}</div>
            <div><span className="font-medium">Employment Date:</span> {staff?.hire_date || 'N/A'}</div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="contact">Contact</TabsTrigger>
              <TabsTrigger value="education">Education</TabsTrigger>
              <TabsTrigger value="work">Work Experience</TabsTrigger>
              <TabsTrigger value="employer">Employer Info</TabsTrigger>
            </TabsList>

            <TabsContent value="contact" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    data-testid="dob-input"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(xxx) xxx-xxxx"
                    data-testid="phone-input"
                  />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street, City, State, Zip"
                  data-testid="address-input"
                />
              </div>

              <div className="space-y-3">
                <Label className="font-medium">Emergency Contacts</Label>
                {formData.emergency_contacts.map((contact, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-2 p-3 border rounded-lg">
                    <Input
                      placeholder="Name"
                      value={contact.name}
                      onChange={(e) => updateEmergencyContact(idx, 'name', e.target.value)}
                    />
                    <Input
                      placeholder="Address"
                      value={contact.address}
                      onChange={(e) => updateEmergencyContact(idx, 'address', e.target.value)}
                    />
                    <Input
                      placeholder="Phone"
                      value={contact.phone}
                      onChange={(e) => updateEmergencyContact(idx, 'phone', e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="education" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>High School Diploma?</Label>
                  <RadioGroup
                    value={formData.has_high_school === null ? '' : formData.has_high_school ? 'yes' : 'no'}
                    onValueChange={(v) => setFormData({ ...formData, has_high_school: v === 'yes' })}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="hs-yes" />
                      <Label htmlFor="hs-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="hs-no" />
                      <Label htmlFor="hs-no">No</Label>
                    </div>
                  </RadioGroup>
                  {formData.has_high_school && (
                    <>
                      <Input
                        placeholder="Date received"
                        value={formData.high_school_date}
                        onChange={(e) => setFormData({ ...formData, high_school_date: e.target.value })}
                      />
                      <Input
                        placeholder="High School Name"
                        value={formData.high_school_name}
                        onChange={(e) => setFormData({ ...formData, high_school_name: e.target.value })}
                      />
                    </>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>GED?</Label>
                  <RadioGroup
                    value={formData.has_ged === null ? '' : formData.has_ged ? 'yes' : 'no'}
                    onValueChange={(v) => setFormData({ ...formData, has_ged: v === 'yes' })}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="ged-yes" />
                      <Label htmlFor="ged-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="ged-no" />
                      <Label htmlFor="ged-no">No</Label>
                    </div>
                  </RadioGroup>
                  {formData.has_ged && (
                    <>
                      <Input
                        placeholder="Date received"
                        value={formData.ged_date}
                        onChange={(e) => setFormData({ ...formData, ged_date: e.target.value })}
                      />
                      <Input
                        placeholder="Issuing Agency"
                        value={formData.ged_agency}
                        onChange={(e) => setFormData({ ...formData, ged_agency: e.target.value })}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="font-medium">Post High School Education</Label>
                  <Button size="sm" variant="outline" onClick={addEducationEntry} data-testid="add-education-entry">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                {formData.education_entries.map((entry, idx) => (
                  <div key={entry.id} className="grid grid-cols-5 gap-2 p-3 border rounded-lg items-end">
                    <Input
                      placeholder="Institution"
                      value={entry.institution}
                      onChange={(e) => updateEducationEntry(idx, 'institution', e.target.value)}
                    />
                    <Input
                      placeholder="Dates Attended"
                      value={entry.dates_attended}
                      onChange={(e) => updateEducationEntry(idx, 'dates_attended', e.target.value)}
                    />
                    <Input
                      placeholder="Major"
                      value={entry.major}
                      onChange={(e) => updateEducationEntry(idx, 'major', e.target.value)}
                    />
                    <Input
                      placeholder="Degree"
                      value={entry.degree}
                      onChange={(e) => updateEducationEntry(idx, 'degree', e.target.value)}
                    />
                    <Button size="icon" variant="ghost" onClick={() => removeEducationEntry(idx)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label>License/Certification Ever Revoked or Denied?</Label>
                <RadioGroup
                  value={formData.license_revoked === null ? '' : formData.license_revoked ? 'yes' : 'no'}
                  onValueChange={(v) => setFormData({ ...formData, license_revoked: v === 'yes' })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="lic-yes" />
                    <Label htmlFor="lic-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="lic-no" />
                    <Label htmlFor="lic-no">No</Label>
                  </div>
                </RadioGroup>
                {formData.license_revoked && (
                  <Input
                    placeholder="Provide date and licensing agency details"
                    value={formData.license_revoked_details}
                    onChange={(e) => setFormData({ ...formData, license_revoked_details: e.target.value })}
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent value="work" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <Label className="font-medium">Early Childhood Related Work Experience</Label>
                <Button size="sm" variant="outline" onClick={addWorkExperience} data-testid="add-work-entry">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {formData.work_experience.map((exp, idx) => (
                <div key={exp.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-sm">Experience #{idx + 1}</span>
                    <Button size="icon" variant="ghost" onClick={() => removeWorkExperience(idx)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Employer"
                      value={exp.employer}
                      onChange={(e) => updateWorkExperience(idx, 'employer', e.target.value)}
                    />
                    <Input
                      placeholder="Phone"
                      value={exp.phone}
                      onChange={(e) => updateWorkExperience(idx, 'phone', e.target.value)}
                    />
                    <Input
                      placeholder="Dates (e.g., 01/2020 - 06/2022)"
                      value={exp.dates}
                      onChange={(e) => updateWorkExperience(idx, 'dates', e.target.value)}
                    />
                  </div>
                  <Input
                    placeholder="Address"
                    value={exp.address}
                    onChange={(e) => updateWorkExperience(idx, 'address', e.target.value)}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Position"
                      value={exp.position}
                      onChange={(e) => updateWorkExperience(idx, 'position', e.target.value)}
                    />
                    <Input
                      placeholder="Days/Week"
                      value={exp.days_per_week}
                      onChange={(e) => updateWorkExperience(idx, 'days_per_week', e.target.value)}
                    />
                    <Input
                      placeholder="Reason for Leaving"
                      value={exp.reason_for_leaving}
                      onChange={(e) => updateWorkExperience(idx, 'reason_for_leaving', e.target.value)}
                    />
                  </div>
                  <Input
                    placeholder="Duties"
                    value={exp.duties}
                    onChange={(e) => updateWorkExperience(idx, 'duties', e.target.value)}
                  />
                </div>
              ))}
              {formData.work_experience.length === 0 && (
                <p className="text-muted-foreground text-center py-8">No work experience added. Click "Add" to add entries.</p>
              )}
            </TabsContent>

            <TabsContent value="employer" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">Section B - Employer Information (completed by licensee)</p>
              
              <div className="space-y-3">
                <div>
                  <Label>Will this person provide care for infants and toddlers?</Label>
                  <RadioGroup
                    value={formData.provides_infant_care === null ? '' : formData.provides_infant_care ? 'yes' : 'no'}
                    onValueChange={(v) => setFormData({ ...formData, provides_infant_care: v === 'yes' })}
                    className="flex gap-4 mt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="infant-yes" />
                      <Label htmlFor="infant-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="infant-no" />
                      <Label htmlFor="infant-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <Label>Will this person transport children in care?</Label>
                  <RadioGroup
                    value={formData.transports_children === null ? '' : formData.transports_children ? 'yes' : 'no'}
                    onValueChange={(v) => setFormData({ ...formData, transports_children: v === 'yes' })}
                    className="flex gap-4 mt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="transport-yes" />
                      <Label htmlFor="transport-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="transport-no" />
                      <Label htmlFor="transport-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <Label>Will this person be counted in staff-to-child ratios?</Label>
                  <RadioGroup
                    value={formData.counted_in_ratios === null ? '' : formData.counted_in_ratios ? 'yes' : 'no'}
                    onValueChange={(v) => setFormData({ ...formData, counted_in_ratios: v === 'yes' })}
                    className="flex gap-4 mt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="ratio-yes" />
                      <Label htmlFor="ratio-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="ratio-no" />
                      <Label htmlFor="ratio-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <Label>Employment Status</Label>
                  <RadioGroup
                    value={formData.employment_status}
                    onValueChange={(v) => setFormData({ ...formData, employment_status: v })}
                    className="flex gap-4 mt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="full_time" id="ft" />
                      <Label htmlFor="ft">Full Time (21+ hrs/wk)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="part_time" id="pt" />
                      <Label htmlFor="pt">Part Time (20 or fewer hrs/wk)</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className="flex justify-between gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint} data-testid="print-staff-record">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button variant="outline" onClick={handleDownload} data-testid="download-staff-record">
              <Download className="w-4 h-4 mr-1" /> Download PDF
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-staff-record">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StaffRecordForm;
